import type { IBusiness } from "@/business/business";
import { canRerunFullPayroll } from "@/core/business/payroll-only";
import { isAdminOrOwner } from "@/user/utils";

// Structural user shape, for the same reason as `PayrollRosterUser` in
// @/payroll/roster/permissions: the auth-layer user types `parentable_type` as a
// plain string, `DetailedUser` narrows it, and this accepts either.
export interface WorkbookAuthorityUser {
  parentable_type?: string;
  role?: string;
}

/**
 * Whether this user has finance access AND authority over the WHOLE workbook.
 *
 * This is the pair of gates the API applies to anything that speaks for the pay
 * period as a whole rather than one department's sheets — requesting approvals,
 * reading or pushing ServiceTitan adjustments. Mirroring it client-side is what
 * keeps a department lead from seeing a control that could only ever 403.
 *
 * `can_rerun_full_payroll` reports the same server-side predicate
 * (`AccessResolver.full_workbook_authority?`) those endpoints check, including its
 * lenient handling of a business that never configured sheet-level access and of
 * an api that predates the field.
 */
export const hasFullWorkbookAuthority = (
  business: IBusiness | null | undefined,
  user: WorkbookAuthorityUser | null | undefined,
) =>
  (isAdminOrOwner(user?.role) || user?.parentable_type === "Tenant") &&
  canRerunFullPayroll(business);
